<template>
	<div class="dashboard-root">
		<v-row v-if="showHerinschrijvingDashboard">
			<v-col cols="12">
				<v-card class="dashboard-widget-card" elevation="2">
					<v-toolbar flat color="primary" dark dense class="dashboard-widget-toolbar">
						<v-icon class="mr-2">mdi-account-reactivate</v-icon>
						<v-toolbar-title class="font-weight-medium">
							Herinschrijving (per sector)
						</v-toolbar-title>
					</v-toolbar>
					<v-card-text v-if="herinschrijvingSchoolyearCaption" class="py-2 pb-0 caption text--secondary">
						{{ herinschrijvingSchoolyearCaption }}
					</v-card-text>
					<v-divider />
					<v-card-text class="pa-0">
						<v-alert
							v-if="sectorError"
							type="error"
							dense
							text
							border="left"
							colored-border
							class="ma-4 mb-0"
						>
							{{ sectorError }}
						</v-alert>
						<v-data-table
							v-else
							:headers="sectorHeaders"
							:items="sectorRows"
							:loading="sectorLoading"
							item-key="sector_key"
							hide-default-footer
							disable-pagination
							dense
							class="dashboard-data-table elevation-0 dashboard-table-fullwidth"
							no-data-text="Geen herinschrijvingen voor dit schooljaar in de geselecteerde scope."
						></v-data-table>
						<div
							v-if="!sectorLoading && !sectorError && sectorSummaryTotal != null"
							class="px-4 py-3 body-2"
						>
							<strong>Totaal studenten:</strong> {{ sectorSummaryTotal }}
							<span class="mx-2">·</span>
							<strong>Prognose gedaan:</strong> {{ sectorSummaryProjected }}
							<span class="mx-2">·</span>
							<strong>Herinschrijving (na prognose):</strong> {{ sectorSummaryRegisteredOfProjected }}
						</div>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>

		<v-row v-if="showHerinschrijvingDashboard" class="mt-2">
			<v-col cols="12">
				<v-card class="dashboard-widget-card" elevation="2">
					<v-toolbar flat color="primary" dark dense class="dashboard-widget-toolbar">
						<v-icon class="mr-2">mdi-bookshelf</v-icon>
						<v-toolbar-title class="font-weight-medium">
							Herinschrijving (per sector en opleiding)
						</v-toolbar-title>
					</v-toolbar>
					<v-card-text v-if="herinschrijvingSchoolyearCaption" class="py-2 pb-0 caption text--secondary">
						{{ herinschrijvingSchoolyearCaption }}
					</v-card-text>
					<v-divider />
					<v-card-text class="pa-0">
						<v-alert
							v-if="studyError"
							type="error"
							dense
							text
							border="left"
							colored-border
							class="ma-4 mb-0"
						>
							{{ studyError }}
						</v-alert>
						<div
							v-else-if="studyLoading && studySectorBlocks.length === 0"
							class="pa-8 text-center"
						>
							<v-progress-circular indeterminate color="primary" size="40" />
						</div>
						<div
							v-else-if="!studyLoading && studySectorBlocks.length === 0"
							class="pa-6 body-2 text--secondary"
						>
							Geen herinschrijvingen voor dit schooljaar in de geselecteerde scope.
						</div>
						<div v-else class="sector-study-groups pa-2">
							<div
								v-for="block in studySectorBlocks"
								:key="block.sector_key"
								class="sector-study-group mb-4"
							>
								<div class="sector-study-group__title subtitle-2 font-weight-medium primary--text px-2 py-2">
									{{ block.sector_name }}
									<span class="text--secondary font-weight-regular">
										— {{ block.sector_total }} studenten · {{ block.sector_projected }} prognose ·
										{{ block.sector_registered_of_projected }} herinschrijving
									</span>
								</div>
								<v-data-table
									:headers="studyHeaders"
									:items="block.study_rows"
									:loading="false"
									item-key="row_key"
									hide-default-footer
									disable-pagination
									dense
									class="dashboard-data-table elevation-0 dashboard-table-fullwidth sector-nested-table"
									:mobile-breakpoint="0"
								>
									<template v-slot:item.study_name="{ item }">
										<span class="study-name-cell">{{ item.study_name }}</span>
									</template>
								</v-data-table>
							</div>
						</div>
						<div
							v-if="!studyLoading && !studyError && studySummaryTotal != null"
							class="px-4 py-3 body-2"
						>
							<strong>Totaal studenten:</strong> {{ studySummaryTotal }}
							<span class="mx-2">·</span>
							<strong>Prognose gedaan:</strong> {{ studySummaryProjected }}
							<span class="mx-2">·</span>
							<strong>Herinschrijving (na prognose):</strong> {{ studySummaryRegisteredOfProjected }}
						</div>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import Axios from "axios";
import { API_ROOT } from "../config";
import {
	isDashboardBlockedRole,
	isDashboardCoordinatorRole,
	isDashboardRegistrationsRole,
	effectiveDashboardRoleId,
	dashboardCoordinatorIdentity,
	dashboardBlockedRedirectPath
} from "../router/authMeta";

const ax = Axios.create({
	baseURL: API_ROOT["osbod_salary"],
	timeout: 60000
});

export default {
	name: "dashboard_herinschrijving",
	data() {
		return {
			dashboardUser: null,
			schoolyearName: null,
			bySector: [],
			sectorSummaryTotal: null,
			sectorSummaryProjected: null,
			sectorSummaryRegisteredOfProjected: null,
			sectorLoading: false,
			sectorError: null,
			sectorsWithStudies: [],
			studySummaryTotal: null,
			studySummaryProjected: null,
			studySummaryRegisteredOfProjected: null,
			studyLoading: false,
			studyError: null,
			sectorHeaders: [
				{ text: "Sector", value: "sector_name", sortable: true },
				{ text: "Totaal studenten", value: "total", sortable: true, align: "end", width: "130px" },
				{ text: "Prognose gedaan", value: "projected", sortable: true, align: "end", width: "140px" },
				{
					text: "Herinschrijving (na prognose)",
					value: "registered_of_projected",
					sortable: true,
					align: "end",
					width: "190px"
				},
				{ text: "Herinschrijving (totaal)", value: "registered", sortable: true, align: "end", width: "160px" }
			],
			studyHeaders: [
				{ text: "Opleiding", value: "study_name", sortable: false },
				{ text: "Totaal studenten", value: "total", sortable: false, align: "end", width: "130px" },
				{ text: "Prognose gedaan", value: "projected", sortable: false, align: "end", width: "140px" },
				{
					text: "Herinschrijving (na prognose)",
					value: "registered_of_projected",
					sortable: false,
					align: "end",
					width: "190px"
				},
				{ text: "Herinschrijving (totaal)", value: "registered", sortable: false, align: "end", width: "160px" }
			]
		};
	},
	computed: {
		dashboardRoleId() {
			return effectiveDashboardRoleId(this.dashboardUser);
		},
		showHerinschrijvingDashboard() {
			return isDashboardRegistrationsRole(this.dashboardRoleId);
		},
		herinschrijvingSchoolyearCaption() {
			var parts = [];
			if (this.schoolyearName) {
				parts.push(this.schoolyearName);
			} else {
				parts.push("Actief schooljaar (herinschrijving)");
			}
			if (isDashboardCoordinatorRole(this.dashboardRoleId)) {
				parts.push("Alleen opleidingen waarvoor u coördinator bent");
			}
			return parts.join(" · ");
		},
		sectorRows() {
			return (this.bySector || []).map(function(row) {
				return {
					sector_key: row.sector_id != null ? "hs" + row.sector_id : "_none",
					sector_name: row.sector_name || "—",
					total: row.total || 0,
					projected: row.projected || 0,
					registered_of_projected: row.registered_of_projected || 0,
					registered: row.registered || 0
				};
			});
		},
		studySectorBlocks() {
			return (this.sectorsWithStudies || []).map(function(sec, si) {
				var studies = sec.studies || [];
				return {
					sector_key: sec.sector_id != null ? "hsec" + sec.sector_id : "_hnone_" + si,
					sector_name: sec.sector_name || "—",
					sector_total: typeof sec.total === "number" ? sec.total : 0,
					sector_projected: typeof sec.projected === "number" ? sec.projected : 0,
					sector_registered_of_projected:
						typeof sec.registered_of_projected === "number" ? sec.registered_of_projected : 0,
					study_rows: studies.map(function(st, ti) {
						return {
							row_key:
								(sec.sector_id != null ? String(sec.sector_id) : "x") +
								"_h_" +
								ti +
								"_" +
								String(st.study_name || ""),
							study_name: st.study_name || "—",
							total: st.total || 0,
							projected: st.projected || 0,
							registered_of_projected: st.registered_of_projected || 0,
							registered: st.registered || 0
						};
					})
				};
			});
		}
	},
	created() {
		try {
			this.dashboardUser = JSON.parse(localStorage.getItem("user") || "null");
		} catch (e) {
			this.dashboardUser = null;
		}
		var rid = this.dashboardRoleId;
		if (isDashboardBlockedRole(rid)) {
			this.$router.replace({ path: dashboardBlockedRedirectPath(rid), replace: true });
			return;
		}
		if (this.showHerinschrijvingDashboard) {
			this.loadHerinschrijvingWidgets();
		}
	},
	methods: {
		dashboardAuthParams() {
			var p = {};
			if (this.dashboardRoleId != null && !Number.isNaN(this.dashboardRoleId)) {
				p.role_id = this.dashboardRoleId;
			}
			var un = dashboardCoordinatorIdentity(this.dashboardUser);
			if (un) {
				p.username = un;
			}
			return p;
		},
		loadHerinschrijvingWidgets() {
			var self = this;
			var params = self.dashboardAuthParams();
			self.sectorLoading = true;
			self.studyLoading = true;
			self.sectorError = null;
			self.studyError = null;

			ax.get("/dashboard/reregistration_counts_by_sector", { params: params })
				.then(function(response) {
					var d = response.data || {};
					self.bySector = d.by_sector || [];
					self.sectorSummaryTotal = typeof d.total === "number" ? d.total : null;
					self.sectorSummaryProjected = typeof d.projected === "number" ? d.projected : null;
					self.sectorSummaryRegisteredOfProjected =
						typeof d.registered_of_projected === "number" ? d.registered_of_projected : null;
					if (d.schoolyear_name) {
						self.schoolyearName = d.schoolyear_name;
					}
					self.sectorLoading = false;
				})
				.catch(function(err) {
					self.sectorLoading = false;
					self.bySector = [];
					self.sectorSummaryTotal = null;
					self.sectorSummaryProjected = null;
					self.sectorSummaryRegisteredOfProjected = null;
					self.sectorError =
						(err.response && err.response.data && err.response.data.error) ||
						"Kon herinschrijvingen per sector niet laden.";
				});

			ax.get("/dashboard/reregistration_counts_by_study", { params: params })
				.then(function(response) {
					var ds = response.data || {};
					self.sectorsWithStudies = ds.sectors || [];
					self.studySummaryTotal = typeof ds.total === "number" ? ds.total : null;
					self.studySummaryProjected = typeof ds.projected === "number" ? ds.projected : null;
					self.studySummaryRegisteredOfProjected =
						typeof ds.registered_of_projected === "number" ? ds.registered_of_projected : null;
					if (ds.schoolyear_name && !self.schoolyearName) {
						self.schoolyearName = ds.schoolyear_name;
					}
					self.studyLoading = false;
				})
				.catch(function(err) {
					self.studyLoading = false;
					self.sectorsWithStudies = [];
					self.studySummaryTotal = null;
					self.studySummaryProjected = null;
					self.studySummaryRegisteredOfProjected = null;
					self.studyError =
						(err.response && err.response.data && err.response.data.error) ||
						"Kon herinschrijvingen per opleiding niet laden.";
				});
		}
	}
};
</script>

<style scoped>
.dashboard-root {
	width: 100%;
	max-width: 100%;
}

.dashboard-widget-card {
	border-radius: 4px;
	overflow: hidden;
}

.dashboard-widget-toolbar {
	border-top-left-radius: 4px;
	border-top-right-radius: 4px;
}

.sector-study-group__title {
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.study-name-cell {
	white-space: nowrap;
}
</style>

<style>
.dashboard-data-table.v-data-table thead tr th {
	background-color: rgba(245, 124, 0, 0.1) !important;
	border-bottom: thin solid rgba(245, 124, 0, 0.35) !important;
	font-weight: 600 !important;
	font-size: 0.8125rem !important;
}

.dashboard-table-fullwidth {
	width: 100% !important;
}

.dashboard-table-fullwidth .v-data-table__wrapper {
	overflow-x: auto;
}
</style>
